import { z } from 'zod/v3';

/**
 * DTOs for Analytics feature
 * Used by web client for type safety
 */

// Event types we track
export const AnalyticsEventType = {
  GUIDE_STARTED: 'guide_started',
  STEP_EXECUTED: 'step_executed', 
  GUIDE_COMPLETED: 'guide_completed',
  GUIDE_ABANDONED: 'guide_abandoned'
} as const;

export type AnalyticsEventTypeValue = typeof AnalyticsEventType[keyof typeof AnalyticsEventType];

// Schema for tracking events from client
export const TrackAnalyticsEventSchema = z.object({
  sessionId: z.string(),
  guideId: z.string(),
  event: z.enum(['guide_started', 'step_executed', 'guide_completed', 'guide_abandoned']),
  
  // Optional step-level data
  stepId: z.string().optional(),
  stepIndex: z.number().optional(),
  success: z.boolean().optional(),
  duration: z.number().optional(), // milliseconds
  retryCount: z.number().optional(),
  errorType: z.string().optional(),
  
  // User context
  userId: z.string().optional(),
  userRole: z.string().optional(),
  pageUrl: z.string().optional()
});

export type TrackAnalyticsEventDTO = z.infer<typeof TrackAnalyticsEventSchema>;

// Guide effectiveness metrics
export interface GuideEffectivenessDTO {
  guideId: string;
  guideName?: string;
  sessions: number;
  completionRate: number;
  successRate: number;
  avgTimeToComplete: number; // milliseconds
  abandonmentPoints: Array<{ 
    stepIndex: number; 
    count: number;
  }>;
}

// Friction point in a guide
export interface FrictionPointDTO {
  stepId: string;
  stepIndex: number;
  guideId?: string;
  guideName?: string;
  retryRate: number; // percentage
  avgTimeOnStep: number; // milliseconds
  errorRate: number; // percentage
  commonErrors: string[];
}

// Usage metrics
export interface UsageMetricsDTO {
  totalSessions: number;
  uniqueUsers: number;
  guidesUsed: number;
  peakHours: Array<{ 
    hour: number; 
    count: number;
  }>;
  avgSessionsPerDay: number;
}

// Time saved metrics
export interface TimeSavedMetricsDTO {
  totalTimeSaved: number; // minutes
  avgTimeSavedPerSession: number; // minutes
  sessionsWithTimeSaved: number;
  estimatedCostSaved?: number; // dollars (optional)
}

// Dashboard metrics response
export interface DashboardMetricsDTO {
  effectiveness: GuideEffectivenessDTO[];
  frictionPoints: FrictionPointDTO[];
  usage: UsageMetricsDTO;
  timeSaved: TimeSavedMetricsDTO;
  periodDays: number;
  lastUpdated: string; // ISO date
}

// Guide-specific metrics response
export interface GuideMetricsDTO {
  guide: GuideEffectivenessDTO | null;
  frictionPoints: FrictionPointDTO[];
  recentSessions: Array<{
    sessionId: string;
    userId?: string;
    startedAt: string;
    completedAt?: string;
    success: boolean;
    stepsCompleted: number;
  }>;
}

// Export analytics data request
export interface ExportAnalyticsRequestDTO {
  format: 'csv' | 'json';
  startDate?: string; // ISO date
  endDate?: string; // ISO date
  guideId?: string;
}

// Analytics event for display in activity feed
export interface AnalyticsEventDTO {
  id: string;
  event: AnalyticsEventTypeValue;
  guideId: string;
  guideName?: string;
  userId?: string;
  stepIndex?: number;
  success?: boolean;
  duration?: number;
  createdAt: string;
}